import React, { useMemo, useRef, useState } from 'react';
import { Sparkles, Loader2, FileUp, X, AlertTriangle, CheckCircle2, CircleHelp } from 'lucide-react';
import Modal from '../Modal';
import { ITEM_KINDS } from '../../constants/upgrades';
import { shrinkPhoto } from '../../utils/aiApi';
import { computeTotals, lineTotal, money, newKey } from '../../utils/upgradesApi';
import { balanceToFinalPrice, netPrice } from '../../utils/vatBalance';

// "Vyplnit pomocí AI": the user uploads a document (request, quote, order, photo…), Claude proposes the lines of the change
// list, the server checks serial numbers and customer against the job, and the user confirms what goes into the form.
// Nothing is saved here: the lines land in the unsaved form and are saved with "Uložit změny".

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.gif,.docx,.txt,.csv,application/pdf,image/*';
const DOCUMENT_KINDS = { request: 'Poptávka / požadavek zákazníka', quote: 'Cenová nabídka', order: 'Objednávka', specification: 'Specifikace', other: 'Dokument' };

const plain = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

async function uploadDocument(jobId, file) {
  const form = new FormData();
  form.append('file', await shrinkPhoto(file));
  const res = await fetch(`/api/upgrades/${jobId}/ai-import`, { method: 'POST', credentials: 'include', body: form });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) throw new Error(json.details?.[0]?.msg || json.error || `Chyba ${res.status}`);
  return json.data;
}

const toRows = (proposal, existing, vatMode) => {
  const known = new Set(existing.map((i) => plain(i.description)));
  return proposal.items.map((i) => {
    const duplicate = known.has(plain(i.description));
    const printed = i.printed_price ?? null;
    return {
      key: newKey(), include: !duplicate, duplicate, kind: i.kind, description: i.description, qty: i.qty, unit: i.unit,
      printed_price: printed, edited: false, unit_price: printed === null ? i.unit_price : netPrice(printed, vatMode, proposal.vat_rate), price_known: i.price_known,
    };
  });
};

// ---------- review of the proposal ----------

const Checks = ({ proposal }) => (
  <div className="upg-ai__checks">
    {proposal.customer.status === 'match' && <span className="upg-ai__ok"><CheckCircle2 size={15} /> Zákazník v dokumentu sedí{proposal.customer.name ? ` (${proposal.customer.name})` : ''}</span>}
    {proposal.customer.status === 'mismatch' && <span className="upg-ai__warn"><AlertTriangle size={15} /> Jiný zákazník v dokumentu{proposal.customer.name ? `: ${proposal.customer.name}` : ''}</span>}
    {proposal.customer.status === null && proposal.customer.name && <span className="upg-ai__unknown"><CircleHelp size={15} /> Zákazník v dokumentu: {proposal.customer.name}</span>}
    {proposal.serials.map((s) => (s.status === 'match'
      ? <span key={s.value} className="upg-ai__ok"><CheckCircle2 size={15} /> SN {s.value} patří k této úpravě</span>
      : <span key={s.value} className="upg-ai__warn"><AlertTriangle size={15} /> SN {s.value} je jiný trenažér</span>))}
  </div>
);

// Whether the document's prices include VAT: the AI proposes, the user decides (documents often do not say it).
const VatMode = ({ proposal, balance, vatMode, onChange }) => {
  const { rows } = balance;
  if (!rows.some((r) => r.printed_price !== null)) return null;
  const totals = computeTotals(rows.filter((r) => r.include && r.description.trim()).map((r) => ({ qty: Number(r.qty) || 0, unit_price: Number(r.unit_price) || 0 })), 0, proposal.vat_rate);
  const compare = vatMode === 'with' ? 'S DPH' : 'Bez DPH';
  return (
    <>
      {proposal.prices_include_vat === null && (
        <div className="upg-banner" role="status"><AlertTriangle size={16} aria-hidden="true" /><span>Dokument neuvádí, jestli jsou ceny s DPH nebo bez DPH. Předpokládám, že jsou s DPH, jinak to přepněte níže.</span></div>
      )}
      <fieldset className="upg-ai__mode">
        <legend className="dlv-label">Ceny v dokumentu jsou</legend>
        <label><input type="radio" name="upg-ai-vat" checked={vatMode === 'with'} onChange={() => onChange('with')} /> s DPH {proposal.vat_rate} % (přepočtou se na ceny bez DPH)</label>
        <label><input type="radio" name="upg-ai-vat" checked={vatMode === 'without'} onChange={() => onChange('without')} /> bez DPH (vloží se tak, jak jsou)</label>
        <span className="upg-muted" style={{ fontSize: 13 }}>Vkládané položky celkem: {money(totals.net)} bez DPH · {money(totals.gross)} s DPH {proposal.vat_rate} %</span>
        {proposal.final_price !== null && balance.status !== 'none' && (
          <span className={balance.status === 'differs' ? 'upg-ai__warn' : 'upg-ai__ok'} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 14 }}>
            {balance.status === 'differs' ? <AlertTriangle size={15} /> : <CheckCircle2 size={15} />}
            {balance.status === 'differs'
              ? `Konečná cena v dokumentu je ${money(proposal.final_price)} (${compare.toLowerCase()}), položky vychází na ${money(balance.total)}. Zkontrolujte položky.`
              : `Konečná cena v dokumentu ${money(proposal.final_price)} (${compare.toLowerCase()}) sedí${balance.status === 'balanced' ? ' (haléřové zaokrouhlení jsem vyrovnal u jedné položky)' : ''}.`}
          </span>
        )}
      </fieldset>
    </>
  );
};

const Review = ({ proposal, balance, onUpdate, options, setOptions, existingCount, hasDescription, vatMode, onVatMode }) => {
  const rows = balance.rows;
  const update = onUpdate;
  return (
    <>
      <p className="upg-muted" style={{ margin: '0 0 10px', fontSize: 14 }}>
        <strong>{DOCUMENT_KINDS[proposal.document_kind] || DOCUMENT_KINDS.other}</strong> · {proposal.file_name}
      </p>
      <Checks proposal={proposal} />
      {proposal.warnings.map((w) => <div key={w} className="upg-banner" role="status"><AlertTriangle size={16} aria-hidden="true" /><span>{w}</span></div>)}
      <VatMode proposal={proposal} balance={balance} vatMode={vatMode} onChange={onVatMode} />

      {proposal.summary && (
        <label className="upg-ai__opt">
          <input type="checkbox" checked={options.useSummary} onChange={(e) => setOptions({ ...options, useSummary: e.target.checked })} />
          <span><strong>{hasDescription ? 'Nahradit zadání úpravy' : 'Použít jako zadání úpravy'}:</strong> {proposal.summary}</span>
        </label>
      )}
      {proposal.discount_percent !== null && (
        <label className="upg-ai__opt">
          <input type="checkbox" checked={options.useDiscount} onChange={(e) => setOptions({ ...options, useDiscount: e.target.checked })} />
          <span><strong>Použít slevu {proposal.discount_percent} %</strong> uvedenou v dokumentu</span>
        </label>
      )}

      {rows.length > 0 && (
        <div className="upg-airbox" style={{ marginTop: 12 }}>
          {rows.map((r, i) => (
            <div key={r.key} className={`upg-air ${r.include ? '' : 'upg-air--off'}`}>
              <label className="upg-air__check"><input type="checkbox" checked={r.include} onChange={(e) => update(r.key, { include: e.target.checked })} aria-label={`Použít položku ${i + 1}`} /></label>
              <select className="dlv-input upg-air__kind" aria-label={`Druh položky ${i + 1}`} value={r.kind} onChange={(e) => update(r.key, { kind: e.target.value })}>
                {ITEM_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
              <input className="dlv-input upg-air__desc" aria-label={`Popis změny ${i + 1}`} value={r.description} maxLength={500} onChange={(e) => update(r.key, { description: e.target.value })} />
              <input className="dlv-input upg-num upg-air__qty" aria-label={`Množství ${i + 1}`} type="number" min="0.01" step="any" value={r.qty} onChange={(e) => update(r.key, { qty: e.target.value })} />
              <input className="dlv-input upg-air__unit" aria-label={`Jednotka ${i + 1}`} value={r.unit} maxLength={10} onChange={(e) => update(r.key, { unit: e.target.value })} />
              <input className={`dlv-input upg-num upg-air__price ${r.price_known || Number(r.unit_price) > 0 ? '' : 'upg-air__price--missing'}`} aria-label={`Cena za jednotku ${i + 1}`} type="number" min="0" step="any"
                placeholder="doplňte" value={r.price_known || Number(r.unit_price) > 0 ? r.unit_price : ''} onChange={(e) => update(r.key, { unit_price: e.target.value, price_known: true, edited: true })} />
              <span className="upg-air__total">{r.price_known || Number(r.unit_price) > 0 ? money(lineTotal({ qty: Number(r.qty) || 0, unit_price: Number(r.unit_price) || 0 })) : '—'}</span>
              {r.duplicate && <span className="upg-flag upg-flag--info upg-air__dup">už je ve výpisu</span>}
            </div>
          ))}
        </div>
      )}

      {existingCount > 0 && rows.length > 0 && (
        <fieldset className="upg-ai__mode">
          <legend className="dlv-label">Stávající položky ve výpisu ({existingCount})</legend>
          <label><input type="radio" name="upg-ai-mode" checked={!options.replace} onChange={() => setOptions({ ...options, replace: false })} /> Přidat k nim</label>
          <label><input type="radio" name="upg-ai-mode" checked={options.replace} onChange={() => setOptions({ ...options, replace: true })} /> Nahradit je (při uložení se smažou)</label>
        </fieldset>
      )}
    </>
  );
};

// ---------- entry point: the bar above the change list + the dialog ----------

const UpgradeAiImport = ({ jobId, existingItems, hasDescription, onApply }) => {
  const fileInput = useRef(null);
  const [over, setOver] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [proposal, setProposal] = useState(null);
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState({ useSummary: false, useDiscount: true, replace: false });
  const [vatMode, setVatMode] = useState('with');

  // The rows as they will be inserted: with the haléř rounding of the VAT conversion balanced to the document's final price
  // (not when a discount is applied on top, then the totals differ by design).
  const balance = useMemo(() => {
    const withDiscount = options.useDiscount && Boolean(proposal?.discount_percent);
    return balanceToFinalPrice(rows, { mode: vatMode, vatRate: proposal?.vat_rate ?? 21, finalPrice: withDiscount ? null : proposal?.final_price ?? null });
  }, [rows, vatMode, proposal, options.useDiscount]);
  const updateRow = (key, patch) => setRows((current) => current.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  // Switching the VAT mode recomputes every line whose price the user has not typed in by hand
  const changeVatMode = (mode) => {
    setVatMode(mode);
    setRows((current) => current.map((r) => (r.edited || r.printed_price === null ? r : { ...r, unit_price: netPrice(r.printed_price, mode, proposal.vat_rate) })));
  };

  const start = async (file) => {
    if (!file) return;
    setOpen(true);
    setBusy(true);
    setError('');
    setProposal(null);
    try {
      const data = await uploadDocument(jobId, file);
      // A document that does not say anything about VAT is taken as "with VAT" (our own calculations are with VAT)
      const mode = data.prices_include_vat === false ? 'without' : 'with';
      setProposal(data);
      setVatMode(mode);
      setRows(toRows(data, existingItems, mode));
      setOptions({ useSummary: Boolean(data.summary) && !hasDescription, useDiscount: true, replace: false });
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const close = () => { if (!busy) { setOpen(false); setProposal(null); } };
  const chosen = balance.rows.filter((r) => r.include && r.description.trim());

  const apply = () => {
    onApply({
      items: chosen.map((r) => ({ kind: r.kind, description: r.description.trim(), qty: Number(r.qty) > 0 ? Number(r.qty) : 1, unit: r.unit || 'ks', unit_price: Number(r.unit_price) || 0 })),
      replace: options.replace,
      description: options.useSummary && proposal?.summary ? proposal.summary : undefined,
      discount: options.useDiscount && proposal?.discount_percent !== null && chosen.length > 0 ? proposal.discount_percent : undefined,
    });
    setOpen(false);
    setProposal(null);
  };

  return (
    <>
      <div className={`upg-ai ${over ? 'upg-ai--over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); start(e.dataTransfer.files?.[0]); }}>
        <Sparkles size={20} aria-hidden="true" style={{ color: 'var(--dlv-red)', flexShrink: 0 }} />
        <span className="upg-ai__text"><strong>Vyplnit pomocí AI:</strong> nahrajte PDF, fotku nebo soubor (poptávka, nabídka, objednávka) a AI navrhne položky výpisu změn. Před vložením je zkontrolujete.</span>
        <button type="button" className="dlv-btn dlv-btn--dark upg-sm" onClick={() => fileInput.current?.click()}><FileUp size={16} /> Nahrát soubor</button>
        <input ref={fileInput} type="file" accept={ACCEPT} hidden onChange={(e) => { start(e.target.files?.[0]); e.target.value = ''; }} />
      </div>

      <Modal show={open} onClose={close}>
        <div className="dlv dlv-card" role="dialog" aria-modal="true" aria-labelledby="upg-ai-title" style={{ width: 'min(940px, calc(100vw - 32px))', maxHeight: '90vh', overflowY: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <h2 id="upg-ai-title" className="dlv-section-head__title" style={{ fontSize: 18, display: 'flex', gap: 8, alignItems: 'center' }}><Sparkles size={18} /> AI načtení dokumentu</h2>
            <button type="button" className="dlv-iconbtn" style={{ marginLeft: 'auto' }} onClick={close} disabled={busy} aria-label="Zavřít"><X size={16} /></button>
          </div>

          {busy && <p className="dlv-hint" role="status" style={{ display: 'flex', gap: 10, alignItems: 'center' }}><Loader2 size={18} className="dlv-spin" /> AI čte dokument, může to trvat i minutu…</p>}
          {error && (
            <>
              <p role="alert" className="upg-error">{error}</p>
              <button type="button" className="dlv-btn" onClick={() => fileInput.current?.click()}><FileUp size={16} /> Zkusit jiný soubor</button>
            </>
          )}
          {proposal && !busy && (
            <Review proposal={proposal} balance={balance} onUpdate={updateRow} options={options} setOptions={setOptions} existingCount={existingItems.length} hasDescription={hasDescription} vatMode={vatMode} onVatMode={changeVatMode} />
          )}

          {proposal && !busy && (
            <div className="upg-ai__foot">
              <span className="upg-muted" style={{ fontSize: 13 }}>Nic se neukládá, dokud nepotvrdíte „Uložit změny“ na stránce úpravy.</span>
              <button type="button" className="dlv-btn" onClick={close}>Zrušit</button>
              <button type="button" className="dlv-btn dlv-btn--primary" disabled={chosen.length === 0 && !(options.useSummary && proposal.summary)} onClick={apply}>
                {chosen.length > 0 ? `Vložit ${chosen.length} ${chosen.length === 1 ? 'položku' : chosen.length < 5 ? 'položky' : 'položek'}` : 'Použít jen zadání'}
              </button>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default UpgradeAiImport;
